import { CoreViewProps, classNames, documentObject, stopEvent, useAlert } from '@fold-dev/core'
import React, { useContext, useRef } from 'react'
import { RichInput, RichInputContext } from '../rich-input'
import { dispatchTodoEvent } from './todo.util'

export type TodoTaskEditorProps = {
    dontClearOnSubmit?: boolean
    selectOnFocus?: boolean
    isAddBelow?: boolean
    isAddAbove?: boolean
    indented?: boolean
    id: string
    title: string
    onSave: (value) => void
    onSaveMultiple?: (value) => void
    onDismiss: () => void
    onIndent?: () => void
    onOutdent?: () => void
    onAddBelowClose?: () => void
} & CoreViewProps

export const TodoTaskEditor = (props: TodoTaskEditorProps) => {
    const {
        dontClearOnSubmit,
        selectOnFocus,
        isAddBelow,
        indented,
        title,
        id,
        onSave,
        onSaveMultiple,
        onDismiss,
        onIndent,
        onOutdent,
        onAddBelowClose,
    } = props
    const ref = useRef(null)
    const { alert } = useAlert()
    const className = classNames(
        {
            'f-todo-task-editor': true,
            'f-row': true,
            'is-indented': indented,
        },
        []
    )

    const openEditInPreviousSibling = () => {
        const element = documentObject.getElementById(id)

        if (element) {
            const { previousSibling } = element
            const siblingId = previousSibling ? previousSibling.dataset.id : null

            if (siblingId) {
                dispatchTodoEvent('edit-task', { id: siblingId })
                onDismiss()
            }
        }
    }

    const openEditInNextSibling = () => {
        const element = documentObject.getElementById(id)

        if (element) {
            const { nextSibling } = element
            const siblingId = nextSibling ? nextSibling.dataset.id : null

            if (siblingId) {
                dispatchTodoEvent('edit-task', { id: siblingId })
                onDismiss()
            }
        }
    }

    const openAddBelowInPreviousSibling = () => {
        const element = documentObject.getElementById(id)

        if (element) {
            const { previousSibling } = element
            const siblingId = previousSibling ? previousSibling.dataset.id : null

            if (siblingId) {
                dispatchTodoEvent('add-task-below', { id: siblingId })
                if (onAddBelowClose) onAddBelowClose()
            }
        }
    }

    const openAddBelowInNextSibling = () => {
        const element = documentObject.getElementById(id)

        if (element) {
            const { nextSibling } = element
            const siblingId = nextSibling ? nextSibling.dataset.id : null

            if (siblingId) {
                dispatchTodoEvent('add-task-below', { id: siblingId })
                if (onAddBelowClose) onAddBelowClose()
            }
        }
    }

    const handleSubmit = (title) => {
        if (title.trim() != '') onSave({ title })
    }

    const handleUp = () => {
        if (isAddBelow) {
            openAddBelowInPreviousSibling()
        } else {
            openEditInPreviousSibling()
        }
    }

    const handleDown = () => {
        if (isAddBelow) {
            openAddBelowInNextSibling()
        } else {
            openEditInNextSibling()
        }
    }

    const handleCancel = () => {
        onDismiss()
    }

    const handlePasteMultiLine = (text) => {
        if (onSaveMultiple) {
            onSaveMultiple(text.split(/\r?\n/))
        } else {
            alert({
                title: 'Sorry',
                description: 'Pasting multiple tasks are not supported here yet.',
            })
            onDismiss()
        }
    }

    return (
        <div
            tabIndex={0}
            className={className}>
            <RichInput
                dontClearOnSubmit={dontClearOnSubmit}
                selectOnFocus={selectOnFocus}
                id="todo-task-editor"
                ref={ref}
                defaultValue={title}
                placeholder="Task title"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                onIndent={onIndent}
                onOutdent={onOutdent}
                onUp={handleUp}
                onDown={handleDown}
                onPasteMultiLine={handlePasteMultiLine}
            />
        </div>
    )
}
